/**
 * Undo and redo for the image review: every change is a new list of finds, and the lists before
 * and after it are kept.
 *
 * Pure, so the component only holds the state. Changing kinds, removing, marking, moving, turning
 * and resizing are each one step; a drag is one step however far it goes, because only its end
 * is recorded.
 */

/** Enough to undo a whole session of corrections, without keeping every list for ever. */
export const HISTORY_LIMIT = 100;

export interface History<T> {
  readonly past: readonly T[];
  readonly present: T;
  readonly future: readonly T[];
}

export const historyOf = <T>(present: T): History<T> => ({ past: [], present, future: [] });

/** A change: the present joins the past, and whatever had been undone is gone. */
export const recorded = <T>(history: History<T>, next: T): History<T> => (next === history.present
  ? history
  : { past: [...history.past, history.present].slice(-HISTORY_LIMIT), present: next, future: [] });

export const undone = <T>(history: History<T>): History<T> => {
  if (!history.past.length) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous, future: [history.present, ...history.future] };
};

export const redone = <T>(history: History<T>): History<T> => {
  if (!history.future.length) return history;
  const [next, ...rest] = history.future;
  return { past: [...history.past, history.present], present: next, future: rest };
};
